/**
 * Impersonation Slice
 * Redux state management for admin user impersonation
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { adminService } from '../../services/admin.service';
import { setImpersonationSessionId, clearImpersonationSession } from '../../services/api.client';
import { logger } from '../../utils/logger';

/**
 * Impersonation session info stored in Redux
 */
export interface ImpersonationSession {
  sessionId: string;
  targetUser: {
    userId: string;
    email: string;
    displayName: string;
  };
  expiresAt: string;
  timeoutMinutes: number;
  startedAt?: string;
  reason?: string;
}

/**
 * Impersonation state
 */
export interface ImpersonationState {
  /** Whether currently impersonating */
  isImpersonating: boolean;
  /** Current session details */
  session: ImpersonationSession | null;
  /** Loading state for impersonation operations */
  isLoading: boolean;
  /** Error message if operation failed */
  error: string | null;
}

/**
 * Initial state
 */
const initialState: ImpersonationState = {
  isImpersonating: false,
  session: null,
  isLoading: false,
  error: null,
};

/**
 * Check impersonation status on app load
 */
export const checkImpersonationStatus = createAsyncThunk(
  'impersonation/checkStatus',
  async (_, { rejectWithValue: _rejectWithValue }) => {
    try {
      logger.debug('Checking impersonation status', {
        event: 'impersonation.status.started',
      });
      const response = await adminService.getImpersonationStatus();
      logger.debug('Checked impersonation status', {
        event: 'impersonation.status.succeeded',
        isImpersonating: !!response?.isImpersonating,
      });
      return response;
    } catch {
      // Don't reject - just means no active session
      logger.debug('No active impersonation session', {
        event: 'impersonation.status.none',
      });
      return { isImpersonating: false };
    }
  }
);

/**
 * Start impersonation session
 */
export const startImpersonation = createAsyncThunk(
  'impersonation/start',
  async (
    { userId, reason, timeoutMinutes }: { userId: string; reason: string; timeoutMinutes?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await adminService.startImpersonation(userId, reason, timeoutMinutes);
      // Store session ID in sessionStorage for API client header injection
      setImpersonationSessionId(response.session.sessionId);
      logger.warn('Impersonation started', {
        event: 'impersonation.started',
        'target.user.id': userId,
        sessionId: response.session.sessionId,
      });
      return response;
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message || error.message || 'Failed to start impersonation';
      logger.error('Failed to start impersonation', error, {
        event: 'impersonation.start.failed',
        'target.user.id': userId,
      });
      return rejectWithValue(message);
    }
  }
);

/**
 * End impersonation session
 */
export const endImpersonation = createAsyncThunk(
  'impersonation/end',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.endImpersonation();
      // Clear session ID from sessionStorage
      clearImpersonationSession();
      logger.info('Impersonation stopped', { event: 'impersonation.stopped' });
      return response;
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message || error.message || 'Failed to end impersonation';
      logger.error('Failed to end impersonation', error, {
        event: 'impersonation.stop.failed',
      });
      return rejectWithValue(message);
    }
  }
);

/**
 * Impersonation slice
 */
const impersonationSlice = createSlice({
  name: 'impersonation',
  initialState,
  reducers: {
    /**
     * Clear impersonation state (e.g., on logout)
     */
    clearImpersonation: (state) => {
      state.isImpersonating = false;
      state.session = null;
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set session from stored value (e.g., from sessionStorage)
     */
    restoreSession: (state, action: PayloadAction<ImpersonationSession>) => {
      state.isImpersonating = true;
      state.session = action.payload;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check status
      .addCase(checkImpersonationStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkImpersonationStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.isImpersonating && action.payload.session) {
          state.isImpersonating = true;
          state.session = {
            sessionId: action.payload.session.sessionId,
            targetUser: {
              userId: action.payload.session.targetUserId,
              email: action.payload.session.targetEmail,
              displayName:
                action.payload.session.targetDisplayName || action.payload.session.targetEmail,
            },
            expiresAt: action.payload.session.expiresAt,
            timeoutMinutes: 0, // Not returned from status endpoint
            startedAt: action.payload.session.startedAt,
            reason: action.payload.session.reason,
          };
        } else {
          state.isImpersonating = false;
          state.session = null;
        }
      })
      .addCase(checkImpersonationStatus.rejected, (state) => {
        state.isLoading = false;
        state.isImpersonating = false;
        state.session = null;
      })
      // Start impersonation
      .addCase(startImpersonation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startImpersonation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isImpersonating = true;
        state.session = {
          sessionId: action.payload.session.sessionId,
          targetUser: action.payload.session.targetUser,
          expiresAt: action.payload.session.expiresAt,
          timeoutMinutes: action.payload.session.timeoutMinutes,
        };
        state.error = null;
      })
      .addCase(startImpersonation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // End impersonation
      .addCase(endImpersonation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(endImpersonation.fulfilled, (state) => {
        state.isLoading = false;
        state.isImpersonating = false;
        state.session = null;
        state.error = null;
      })
      .addCase(endImpersonation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearImpersonation, clearError, restoreSession } = impersonationSlice.actions;

export default impersonationSlice.reducer;
